import { Typography, Grid, Divider } from '@mui/material';
import { useQuery } from '@apollo/client';
import { DirectoryAvatar } from 'Theme/components/Avatar';
import Skeleton from 'Theme/components/Skeleton';
import { DataRow } from 'Theme/components/DataRow';
import TestIds from 'Tests/TestIds';
import { useStyles } from '../Directories.styles';
import DirectoryExtraInfoBlock from '../DirectoryExtraInfoBlock';
import EditIdentityIdentifier from '../DirectoryExtraInfoBlock/EditIdentityIdentifier';
import { GetDirectoriesExtendedInfo } from 'Apollo';
import { Imgs } from 'Assets';
import MergeableDirectory from './MergeableDirectory/MergeableDirectory';
import useNotification from 'Hooks/useNotification';
import UnroutableDirectory from './UnroutableDirectory/UnroutableDirectory';

interface Props {
  person: IPersonWithAccount;
  isCurrentPerson: boolean;
  isOrganizationOwner: boolean;
  hasSuperRights: boolean;
  canWriteAll?: boolean;
  organizationCode?: string;
}

function PersonDirectories(props: Readonly<Props>) {
  const classes = useStyles();
  const { person, hasSuperRights, isOrganizationOwner, isCurrentPerson, organizationCode, canWriteAll = false } = props;
  const { onError } = useNotification();

  let { data, loading } = useQuery<
    GetDirectoriesExtendedInfoData,
    GetDirectoriesExtendedInfoVars
  >(GetDirectoriesExtendedInfo, {
    variables: {
      personImmutableId: person.immutableId
    },
    onError
  });

  if (loading || data?.directoriesAuthenticatingPerson == null)
    return (
      <DataRow
        label={
          <Skeleton
            component="span"
            variant="rectangular"
            width="64px"
            height="64px"
          />
        }
        divider={true}
      >
        <Skeleton variant="rectangular" width="200px" />
      </DataRow>
    );

  const directoriesAuthRoutable = data.directoriesAuthenticatingPerson.filter(
    directory => directory.isRoutable
  );
  const directoriesAuthUnroutable = data.directoriesAuthenticatingPerson.filter(
    directory => !directory.isRoutable
  );

  /* shouldn't already have a federated identity */
  const hasARoutableFederatedDirectory = directoriesAuthRoutable.some(
    (d: IDirectoryAuthenticatingPersonType) => d.isFederated && d.isRoutable
  );

  const isFederatedWithGoogle = directoriesAuthRoutable.some(
    (d: IDirectoryAuthenticatingPersonType) => d.label === 'Google'
  );

  const isFederatedWithMicrosoft = directoriesAuthRoutable.some(
    (d: IDirectoryAuthenticatingPersonType) => d.label === 'Microsoft'
  );

  const isFederatedWithApple = directoriesAuthRoutable.some(
    (d: IDirectoryAuthenticatingPersonType) => d.label === 'Apple'
  );

  /* -- To be able to merge a local account with a MS account the current user -- */
  /* shouldn't have an email address that could be managed by a federabled directory */
  /* example even if toto@federatedDirectory.com have no identity, his directory different from MS is federated */
  /* a merge can also be performed if the user has a local account and an identity on a directory which is not routable */
  const userEmailManagedByFederatedDirectoryNotMS =
    hasARoutableFederatedDirectory && !isFederatedWithMicrosoft;
  const userEmailManagedByFederatedDirectoryNotGoogle =
    hasARoutableFederatedDirectory && !isFederatedWithGoogle;
  const userEmailManagedByFederatedDirectoryNotApple =
    hasARoutableFederatedDirectory && !isFederatedWithApple;

  const canMergeLocalAccountToMS =
    isCurrentPerson &&
    !hasARoutableFederatedDirectory &&
    !userEmailManagedByFederatedDirectoryNotMS;
  const canMergeLocalAccountToGoogle =
    isCurrentPerson &&
    !hasARoutableFederatedDirectory &&
    !userEmailManagedByFederatedDirectoryNotGoogle;
  const canMergeLocalAccountToApple =
    isCurrentPerson &&
    !hasARoutableFederatedDirectory &&
    !userEmailManagedByFederatedDirectoryNotApple;

  return (
    <Grid
      container
      direction="column"
      data-testid={TestIds.pages.profile.directories.directoriesList}
    >
      {directoriesAuthRoutable.map((directory, index) => {
        const cegidPasseportLabel = 'Cegid Passeport';

        const isCegidId = directory.label === 'CegidID';
        const isCegidPasseport = directory.label === cegidPasseportLabel;
        const isApple = directory.label === 'Apple';
        const isGoogle = directory.label === 'Google';
        const isMicrosoft = directory.label === 'Microsoft';
        const isNotLast = index < (directoriesAuthRoutable.length - 1)
        const canSeeIdentityIdentifier = hasSuperRights || (isOrganizationOwner && directory.isFederated);
        const canEditIdentityIdentifier = canWriteAll || (isOrganizationOwner && directory.isFederated);

        return (
          <>
            <Grid
              key={directory.identityIdentifier}
              item
              container
              direction="row"
              alignItems="center"
              wrap="nowrap"
              data-testid={
                TestIds.pages.profile.directories.directoriesListItem + index
              }
            >
              <Grid item>
                {isMicrosoft && (
                  <img
                    src={Imgs.microsoft.src}
                    alt={Imgs.microsoft.alt}
                    className={classes.logo}
                    data-testid={TestIds.pages.profile.mergeAccount.MSlogo}
                  />
                )}
                {(isCegidPasseport || isCegidId) && (
                  <img
                    src={Imgs.cegidCorners.src}
                    alt={Imgs.cegidCorners.alt}
                    className={classes.logo}
                    data-testid={TestIds.pages.profile.mergeAccount.CegidLogo}
                  />
                )}
                {isGoogle && (
                  <img
                    src={Imgs.google.src}
                    alt={Imgs.google.alt}
                    className={classes.logo}
                    data-testid={TestIds.pages.profile.mergeAccount.GoogleLogo}
                  />
                )}
                {isApple && (
                  <img
                    src={Imgs.apple.src}
                    alt={Imgs.apple.alt}
                    className={classes.logo}
                    data-testid={TestIds.pages.profile.mergeAccount.AppleLogo}
                  />
                )}
                {!isMicrosoft &&
                  !isCegidPasseport &&
                  !isCegidId &&
                  !isGoogle &&
                  !isApple && (
                    <DirectoryAvatar directory={directory} size="larger" />
                  )}
              </Grid>

              <Grid item>
                <Typography variant="body2">
                  {directory.label !== null &&
                    (isCegidId ? cegidPasseportLabel : directory.label)}
                </Typography>
              </Grid>

              {canSeeIdentityIdentifier && (
                  <DirectoryExtraInfoBlock
                    directory={directory}
                    index={index}
                  />
              )}

              {canEditIdentityIdentifier && directory.isFederated && !!directory.identityIdentifier && (
                <Grid item sx={{ mr: 2 }}>
                  <EditIdentityIdentifier
                    identity={directory.identityIdentifier}
                    personIdentifier={person.immutableId}
                    organizationCode={organizationCode ?? ''}
                    directoryCode={directory.code}
                  />
                </Grid>
              )}
            </Grid>
            {isNotLast && <Divider variant="fullWidth" />}
          </>
        );
      })}
      {canMergeLocalAccountToMS && (
        <>
          <Divider variant="fullWidth" />
          <MergeableDirectory dirKey="microsoft" person={person} />
        </>
      )}
      {canMergeLocalAccountToGoogle && (
        <>
          <Divider variant="fullWidth" />
          <MergeableDirectory dirKey="google" person={person} />
        </>
      )}
      {canMergeLocalAccountToApple && (
        <>
          <Divider variant="fullWidth" />
          <MergeableDirectory dirKey="apple" person={person} />
        </>
      )}
      {directoriesAuthUnroutable.map((directory, index) => {
        return (
          <>
            <Divider variant="fullWidth" />
            <UnroutableDirectory
              key={directory.identityIdentifier}
              directory={directory}
              hasSuperRights={hasSuperRights}
              index={index}
            />
          </>
        );
      })}
    </Grid>
  );
}

export default PersonDirectories;
